import { useQuery, keepPreviousData } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { addDays, startOfDay } from "date-fns";
import { useTenant } from "@/hooks/useTenant";
import { fetchAllRows } from "@/lib/fetch-all-rows";
import { fetchLegStatusSummaries } from "@/lib/order-leg-summaries";
import {
  buildLegEndpointOr,
  buildOrderEndpointClauses,
  legOnlyOrderIds,
  parentEndpointSelect,
  withLegOrderIds,
  type ParentEndpointColumns,
} from "@/lib/order-endpoint-filters";
import {
  Order,
  OrderStatus,
  Customer,
  Tractor,
  Driver,
  Location,
  OrderLegStatusSummary,
} from "@/types/tms";

export type DateRange = { from: Date; to: Date } | null;

export const ORDERS_PAGE_SIZE_OPTIONS = [10, 20, 30, 40, 50] as const;
export const DEFAULT_ORDERS_PAGE_SIZE = 20;

interface UseOrdersQueryParams {
  searchQuery: string;
  statusFilter: string | string[];
  currentPage: number;
  dateFilter: DateRange;
  tenderFilter?: string;
  bolFilter?: string;
  tenderIdFilter?: string;
  trailerTypeFilter?: string | string[];
  trailerLoadStatusFilter?: string | string[];
  apiCreatedFilter?: string;
  tripTypeFilter?: string | string[];
  orderTypeFilter?: string;
  shipFromLocationFilter?: string | string[];
  shipToLocationFilter?: string | string[];
  customerFilter?: string | string[];
  pageSize?: number;
  locations?: Location[];
  /** When provided, restrict results to this set of order IDs (used by "Subscribed orders" filter). Empty array = no matches. */
  subscribedOrderIds?: string[];
  /** Prevent execution until filter lookup data (locations/subscriptions) is ready. */
  dependenciesReady?: boolean;
  /** A failed filter dependency must surface as an error, never as zero matches. */
  dependencyError?: Error | null;
}

type QueryResponse<T> = { data: T | null; error: { message?: string } | null };

export function requireQueryData<T>(response: QueryResponse<T>, context: string): T {
  if (response.error) {
    throw new Error(response.error.message || `Unable to load ${context}`);
  }
  return response.data ?? ([] as unknown as T);
}

export function useOrdersQuery({ searchQuery, statusFilter, currentPage, dateFilter, tenderFilter = "all", bolFilter = "all", tenderIdFilter = "all", trailerTypeFilter = "all", trailerLoadStatusFilter = "all", apiCreatedFilter = "all", tripTypeFilter = "all", orderTypeFilter = "all", shipFromLocationFilter = "all", shipToLocationFilter = "all", customerFilter = "all", pageSize = DEFAULT_ORDERS_PAGE_SIZE, locations, subscribedOrderIds, dependenciesReady = true, dependencyError = null }: UseOrdersQueryParams) {
  // Normalize each location filter to an array of selected location ids.
  // "all", ["all"], or [] are treated as no filter applied.
  const normalize = (f: string | string[]) => {
    const raw = Array.isArray(f) ? f : [f];
    return raw.filter((v) => v && v !== "all");
  };
  const selectedShipFromIds = normalize(shipFromLocationFilter);
  const selectedShipToIds = normalize(shipToLocationFilter);
  const selectedStatuses = normalize(statusFilter);
  const selectedTripTypes = normalize(tripTypeFilter);
  const selectedCustomerIds = normalize(customerFilter);
  const selectedTrailerTypeIds = normalize(trailerTypeFilter);
  const selectedTrailerLoadStatuses = normalize(trailerLoadStatusFilter);
  const shipFromKey = selectedShipFromIds.slice().sort().join(",") || "all";
  const shipToKey = selectedShipToIds.slice().sort().join(",") || "all";
  const statusKey = selectedStatuses.slice().sort().join(",") || "all";
  const tripTypeKey = selectedTripTypes.slice().sort().join(",") || "all";
  const customerKey = selectedCustomerIds.slice().sort().join(",") || "all";
  const trailerTypeKey = selectedTrailerTypeIds.slice().sort().join(",") || "all";
  const trailerLoadStatusKey = selectedTrailerLoadStatuses.slice().sort().join(",") || "all";
  const subscribedKey = subscribedOrderIds ? subscribedOrderIds.slice().sort().join(",") || "none" : "off";
  const { tenantId, tenantReady } = useTenant();
  const { data: ordersData, isLoading, isFetching, isError, error, refetch } = useQuery({
    queryKey: ["orders", tenantId, searchQuery, statusKey, currentPage, pageSize, dateFilter?.from.toISOString(), dateFilter?.to.toISOString(), tenderFilter, bolFilter, tenderIdFilter, trailerTypeKey, trailerLoadStatusKey, apiCreatedFilter, tripTypeKey, orderTypeFilter, shipFromKey, shipToKey, customerKey, subscribedKey],
    enabled: tenantReady && !!tenantId && dependenciesReady && !dependencyError,
    placeholderData: keepPreviousData,
    queryFn: async () => {
      if (subscribedOrderIds) {
        if (subscribedOrderIds.length === 0) {
          return { orders: [], totalCount: 0 };
        }
      }
      // Document presence/absence filters are expressed as join embeds so we
      // never pull the whole order_bol_images / order_tender_images table into
      // the browser just to build an id list (that produced ~100KB URLs and a
      // failed fetch for `bol=missing`).
      const embeds = [
        bolFilter === "has"
          ? "order_bol_images!inner(order_id)"
          : bolFilter === "missing"
            ? "order_bol_images!left(order_id)"
            : null,
        tenderFilter === "has"
          ? "order_tender_images!inner(order_id)"
          : tenderFilter === "missing"
            ? "order_tender_images!left(order_id)"
            : null,
        // Single-leg / multi-leg is derived from `legs` the same way: a parent
        // is multi-leg exactly when it has a leg at sequence 2 (at most one
        // such row per order, so the join can't duplicate parents). Reading the
        // ids into the browser and passing them back as an `in.(...)` list
        // produced a >40KB URL (1200+ parents) and the request was rejected.
        orderTypeFilter === "multi_leg"
          ? "legs!inner(order_id)"
          : orderTypeFilter === "single_leg"
            ? "legs!left(order_id)"
            : null,
      ].filter(Boolean) as string[];

      // Keep the paginated/count query lean. Related display rows are hydrated
      // only for the visible page after the base request succeeds.
      const selectClause = ["*", ...embeds].join(",");

      let query = supabase
        .from("orders")
        .select(selectClause, { count: "exact" })
        .eq("tenant_id", tenantId)
        .eq("is_work_order", false)
        .is("parent_order_id", null);

      if (subscribedOrderIds && subscribedOrderIds.length > 0) {
        query = query.in("id", subscribedOrderIds);
      }

      // Anti-join: filter on the embedded RESOURCE (not one of its columns).
      // Filtering `order_bol_images.order_id` only prunes the embedded rows and
      // leaves every parent order in the result.
      if (bolFilter === "missing") {
        query = query.is("order_bol_images", null);
      }
      if (tenderFilter === "missing") {
        query = query.is("order_tender_images", null);
      }

      if (selectedStatuses.length > 0) {
        query = query.in("status", selectedStatuses as OrderStatus[]);
      }

      if (selectedCustomerIds.length > 0) {
        query = query.in("billing_party_id", selectedCustomerIds);
      }

      if (searchQuery.trim()) {
        const search = searchQuery.trim();
        // Phase 2 (per-leg trailers): also match against `legs.trailer_id`
        // so multi-leg orders whose non-mirrored legs carry the search
        // trailer surface in the results.
        const [customerRes, driverRes, tractorRes, legTrailerRes] = await Promise.all([
          supabase.from("customers").select("id").ilike("organization_name", `%${search}%`),
          supabase.from("drivers").select("id").ilike("name", `%${search}%`),
          supabase.from("tractors").select("id").ilike("name", `%${search}%`),
          supabase
            .from("legs" as any)
            .select("order_id")
            .eq("tenant_id", tenantId)
            .ilike("trailer_id", `%${search}%`)
            .limit(500),
        ]);

        let orParts = [
          `unique_id.ilike.%${search}%`,
          `ship_from_city.ilike.%${search}%`,
          `ship_to_city.ilike.%${search}%`,
          `trailer_id.ilike.%${search}%`,
          `tender_id.ilike.%${search}%`,
        ];

        const customerRows = requireQueryData(customerRes, "matching customers");
        const driverRows = requireQueryData(driverRes, "matching drivers");
        const tractorRows = requireQueryData(tractorRes, "matching tractors");
        const matchingLegRows = requireQueryData(legTrailerRes, "matching order legs");
        const customerIds = customerRows.map(c => c.id);
        const driverIds = driverRows.map(d => d.id);
        const tractorIds = tractorRows.map(t => t.id);

        if (customerIds.length > 0) orParts.push(`billing_party_id.in.(${customerIds.join(",")})`);
        if (driverIds.length > 0) orParts.push(`assigned_operator_id.in.(${driverIds.join(",")})`);
        if (tractorIds.length > 0) orParts.push(`assigned_tractor_id.in.(${tractorIds.join(",")})`);
        const legOrderIds = [...new Set((matchingLegRows as any[]).map((r) => r.order_id).filter(Boolean) as string[])];
        if (legOrderIds.length > 0) orParts.push(`id.in.(${legOrderIds.join(",")})`);

        query = query.or(orParts.join(","));
      }

      if (dateFilter) {
        // Overlap match: a trip belongs to the selected days when it starts
        // before the range ends AND finishes after the range begins. Matching
        // only `ready_time` hid trips that started the day before and deliver
        // inside the range.
        const rangeStart = startOfDay(dateFilter.from).toISOString();
        const rangeEnd = startOfDay(addDays(dateFilter.to, 1)).toISOString();
        query = query
          .lt("ready_time", rangeEnd)
          .gte("requested_delivery_time", rangeStart);
      }

      // Tender ID filter (tender_id is a text column on orders)
      if (tenderIdFilter === "has") {
        query = query.not("tender_id", "is", null);
      } else if (tenderIdFilter === "missing") {
        query = query.is("tender_id", null);
      }

      // Load Tender / BOL presence filters are handled by the embeds above.

      // Trailer Type filter
      if (selectedTrailerTypeIds.length > 0) {
        query = query.in("trailer_type_id", selectedTrailerTypeIds);
      }

      // Trailer Load Status filter — resolve trailer_id text values whose
      // matching trailers row has the selected load_status, then filter orders.
      if (selectedTrailerLoadStatuses.length > 0) {
        const trailerResponse = await supabase
          .from("trailers")
          .select("trailer_id, load_status")
          .eq("tenant_id", tenantId)
          .in("load_status", selectedTrailerLoadStatuses as any);
        const trailerRows = requireQueryData(trailerResponse, "trailers by load status");
        const matchingTrailerIds = [
          ...new Set(trailerRows.map((t) => t.trailer_id).filter(Boolean) as string[]),
        ];
        if (matchingTrailerIds.length === 0) {
          return { orders: [], totalCount: 0 };
        }
        // Phase 2: match either `orders.trailer_id` (leg-1 mirror) OR any
        // `legs.trailer_id`. Resolve leg-matching order ids up-front and
        // combine both via a PostgREST OR clause.
        const legQueryForLoad: any = supabase
          .from("legs" as any)
          .select("order_id");
        const legLoadResponse = await legQueryForLoad
          .eq("tenant_id", tenantId)
          .in("trailer_id", matchingTrailerIds)
          .limit(1000);
        const legRowsForLoad = requireQueryData(legLoadResponse, "order legs by trailer load status");
        const legOrderIdsForLoad = [
          ...new Set(((legRowsForLoad ?? []) as any[]).map((r) => r.order_id).filter(Boolean) as string[]),
        ];
        const trailerInList = matchingTrailerIds.map((t) => t.replace(/[,()]/g, "")).join(",");
        if (legOrderIdsForLoad.length > 0) {
          query = query.or(
            `trailer_id.in.(${trailerInList}),id.in.(${legOrderIdsForLoad.join(",")})`,
          );
        } else {
          query = query.in("trailer_id", matchingTrailerIds);
        }
      }

      // API-created filter
      if (apiCreatedFilter === "api") {
        query = query.eq("is_api_created", true);
      } else if (apiCreatedFilter === "manual") {
        query = query.eq("is_api_created", false);
      }

      // Trip type filter
      if (selectedTripTypes.length > 0) {
        query = query.in("trip_type", selectedTripTypes as any);
      }

      // Order type filter (single-leg vs multi-leg), resolved server-side via
      // the `legs` embed declared above. Works for both legacy parents (child
      // rows in `orders`, mirrored into `legs`) and new parents (rows only in
      // `legs`).
      if (orderTypeFilter === "single_leg" || orderTypeFilter === "multi_leg") {
        query = query.eq("legs.leg_sequence", 2);
        if (orderTypeFilter === "single_leg") {
          // Anti-join on the embedded RESOURCE: keep parents with no leg 2.
          query = query.is("legs", null);
        }
      }

      // Build a ship-from/ship-to filter that mirrors `resolveOrderEndpoint`
      // against the parent order columns, then widen it with any leg whose
      // endpoint matches — a middle stop of a multi-leg trip counts as both an
      // origin and a destination.
      const buildSideOr = async (side: "from" | "to", selectedIds: string[]) => {
        const selectedLocs = (locations ?? []).filter((l) => selectedIds.includes(l.id));
        if (selectedLocs.length === 0) return null;

        const orderClauses = buildOrderEndpointClauses(side, selectedLocs);
        const legOr = buildLegEndpointOr(side, selectedLocs);
        let legOrderIds: string[] = [];
        if (legOr) {
          // Apply the same date window to the leg lookup. Without it a busy
          // terminal resolves thousands of order ids, and inlining those into
          // the orders URL blows past the request-length limit ("Failed to
          // fetch"). Legs carry their own ready/delivery times.
          // The parent status filter is applied through the `orders!inner`
          // embed so legs of filtered-out orders never reach the id list.
          const legRows = await fetchAllRows<{ order_id: string; orders: ParentEndpointColumns | null }>(() => {
            let q = supabase
              .from("legs")
              .select(`order_id, orders!inner(${parentEndpointSelect(side)})`)
              .eq("tenant_id", tenantId)
              .or(legOr);
            if (selectedStatuses.length > 0) {
              q = q.in("orders.status", selectedStatuses as OrderStatus[]);
            }
            if (dateFilter) {
              q = q
                .lt("ready_time", startOfDay(addDays(dateFilter.to, 1)).toISOString())
                .gte("requested_delivery_time", startOfDay(dateFilter.from).toISOString());
            }
            return q;
          });
          // Only inline ids the parent clauses don't already match (leg 1's
          // pickup / last leg's drop are mirrored onto the parent row).
          legOrderIds = legOnlyOrderIds(side, legRows ?? [], selectedLocs);
        }
        return withLegOrderIds(orderClauses, legOrderIds);
      };

      if (selectedShipFromIds.length > 0) {
        const orStr = await buildSideOr("from", selectedShipFromIds);
        if (!orStr) return { orders: [], totalCount: 0 };
        query = query.or(orStr);
      }

      if (selectedShipToIds.length > 0) {
        const orStr = await buildSideOr("to", selectedShipToIds);
        if (!orStr) return { orders: [], totalCount: 0 };
        query = query.or(orStr);
      }

      const from = (currentPage - 1) * pageSize;
      const to = from + pageSize - 1;

      query = query
        .order("ready_time", { ascending: true })
        .range(from, to);

      const { data, error, count } = await query;
      if (error) throw error;

      // Phase 4: enrich each parent order with `leg_count` from public.legs so the
      // list view can render the multi-leg expand chevron without a per-row query.
      const parentIds = (data ?? []).map((o: any) => o.id).filter(Boolean) as string[];
      const legCountByParent = new Map<string, number>();
      const customerIds = [...new Set((data ?? []).map((o: any) => o.billing_party_id).filter(Boolean) as string[])];
      const tractorIds = [...new Set((data ?? []).map((o: any) => o.assigned_tractor_id).filter(Boolean) as string[])];
      const driverIds = [...new Set((data ?? []).flatMap((o: any) => [o.assigned_operator_id, o.assigned_operator_2_id]).filter(Boolean) as string[])];

      const [legStatusesByParent, customerResponse, tractorResponse, driverResponse] = await Promise.all([
        // Shared helper: each leg's own status signals, so the list can render
        // the leg-derived rollup badge (incl. "Partially Confirmed").
        fetchLegStatusSummaries(parentIds, tenantId),
        customerIds.length > 0
          ? (supabase.from("customers") as any).select("*").in("id", customerIds)
          : Promise.resolve({ data: [], error: null }),
        tractorIds.length > 0
          ? (supabase.from("tractors") as any).select("*").in("id", tractorIds)
          : Promise.resolve({ data: [], error: null }),
        driverIds.length > 0
          ? (supabase.from("drivers") as any).select("*").in("id", driverIds)
          : Promise.resolve({ data: [], error: null }),
      ]);

      const customerRows = requireQueryData<Customer[]>(customerResponse as QueryResponse<Customer[]>, "visible order customers");
      const tractorRows = requireQueryData<Tractor[]>(tractorResponse as QueryResponse<Tractor[]>, "visible order tractors");
      const driverRows = requireQueryData<Driver[]>(driverResponse as QueryResponse<Driver[]>, "visible order drivers");

      for (const [orderId, list] of legStatusesByParent) {
        legCountByParent.set(orderId, list.length);
      }


      const customersById = new Map(customerRows.map((row: Customer) => [row.id, row]));
      const tractorsById = new Map(tractorRows.map((row: Tractor) => [row.id, row]));
      const driversById = new Map(driverRows.map((row: Driver) => [row.id, row]));
      const enriched = (data ?? []).map((o: any) => ({
        ...o,
        billing_party: customersById.get(o.billing_party_id),
        assigned_tractor: tractorsById.get(o.assigned_tractor_id) ?? null,
        assigned_operator: driversById.get(o.assigned_operator_id) ?? null,
        assigned_operator_2: driversById.get(o.assigned_operator_2_id) ?? null,
        leg_count: legCountByParent.get(o.id) ?? 0,
        legs: legStatusesByParent.get(o.id) ?? [],
      }));

      return {
        orders: enriched as (Order & {
          billing_party: Customer;
          assigned_tractor: Tractor | null;
          assigned_operator: Driver | null;
          leg_count: number;
          legs: OrderLegStatusSummary[];
        })[],
        totalCount: count || 0,
      };
    },
  });

  const orders = ordersData?.orders;
  const totalCount = ordersData?.totalCount || 0;
  const totalPages = Math.max(1, Math.ceil(totalCount / pageSize));

  // The tenant hasn't resolved yet (or failed to resolve): the query is
  // disabled, so `isLoading` is false and the caller would otherwise render
  // an empty list as if the tenant genuinely had no orders.
  const tenantPending = !tenantReady || !tenantId;
  const dependencyPending = !dependenciesReady && !dependencyError;
  const effectiveError = dependencyError ?? (error as Error | null);

  return {
    orders,
    totalCount,
    totalPages,
    isLoading: isLoading || tenantPending || dependencyPending,
    isFetching,
    isError: isError || !!dependencyError,
    error: effectiveError,
    refetch,
  };
}
